import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowBackIcon,
  AutoAwesomeIcon,
  ChevronRightIcon,
  PlayIcon,
  SearchIcon,
  SearchOffIcon,
  VideoIcon,
} from '../components/icons.js';
import { Layout } from '../components/Layout.js';
import { useAuth } from '../auth/AuthProvider.js';
import { apiGet } from '../api/client.js';
import {
  VideoCard,
  VideoRow,
  VideoThumb,
  type VideoCardData,
} from '../components/video/VideoCard.js';
import { AskPanel } from '../components/video/AskPanel.js';
import { specColor, vfmt } from '../lib/video-format.js';

/**
 * /videos — the user-facing Training Videos library.
 *
 *   Layout (matches the design handoff bundle, OneView Galaxy tokens):
 *     ─ Sticky toolbar: search + spec filter chips + view toggle +
 *       "Ask the trainer" button.
 *     ─ When idle: <HeroFeature> + Continue Watching row + one row per
 *       series.
 *     ─ When filtering / searching: flat grid (auto-fill 244px tiles).
 *
 * Open to every authenticated role. Ring 0 videos are pre-filtered by the
 * backend (`GET /api/v1/videos` already enforces the isInternalRole gate).
 *
 * "Ask the trainer" opens the in-page <AskPanel> drawer; the hero's "Ask
 * about it" opens the same drawer scoped to that one video.
 *
 * `?seriesId=<id>` (the "See series" links) narrows the grid to that
 * series in episode order, with a way back to the full library.
 */

interface Video extends VideoCardData {
  status: 'pending_upload' | 'transcoding' | 'transcribing' | 'summarising' | 'indexed' | 'failed';
  ring: 'ring0' | 'ring1';
  isFeatured?: boolean;
  publishedAt?: string;
  uploadedAt?: string;
  description?: string;
}

interface VideoSeries {
  id: string;
  title: string;
  subtitle?: string;
  specId?: string;
}

interface VideoProgress {
  videoId: string;
  lastPositionSeconds: number;
  percentComplete: number;
  lastWatchedAt: string;
}

export function VideoLibraryPage(): JSX.Element {
  const { user } = useAuth();
  const [videos, setVideos] = useState<Video[]>([]);
  const [series, setSeries] = useState<VideoSeries[]>([]);
  const [progress, setProgress] = useState<Map<string, VideoProgress>>(new Map());
  const [q, setQ] = useState('');
  const [specFilter, setSpecFilter] = useState<string>('all');
  const [view, setView] = useState<'rows' | 'grid'>('rows');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // null = drawer closed; `video` set = scoped to that video ("This video").
  const [ask, setAsk] = useState<{ video?: Video } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const seriesFilter = searchParams.get('seriesId');
  const navigate = useNavigate();

  // Track unmount so an in-flight Promise.all that resolves after unmount
  // doesn't try to setState (mirrors the pattern in VideoCard.tsx). Plain
  // useEffect cleanup with a local flag is enough — no AbortController
  // needed because apiGet doesn't accept a signal today.
  const cancelled = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [vRes, sRes, pRes] = await Promise.all([
        apiGet<{ videos: Video[] }>('/api/v1/videos'),
        apiGet<{ series: VideoSeries[] }>('/api/v1/video-series'),
        apiGet<{ progress: VideoProgress[] }>('/api/v1/me/video-progress'),
      ]);
      if (cancelled.current) return;
      // Only show indexed videos in the user library — pending / failed
      // belong on the admin upload page.
      setVideos(vRes.videos.filter((v) => v.status === 'indexed'));
      setSeries(sRes.series);
      const map = new Map<string, VideoProgress>();
      for (const p of pRes.progress) map.set(p.videoId, p);
      setProgress(map);
    } catch (e) {
      if (cancelled.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load videos');
    } finally {
      if (!cancelled.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelled.current = false;
    void refresh();
    return () => {
      cancelled.current = true;
    };
  }, [refresh]);

  // Enrich each Video with the per-user progress so the cards know what
  // to render (Continue Watching progress bar, Watched badge).
  const videosWithProgress: Video[] = useMemo(
    () =>
      videos.map((v) => {
        const p = progress.get(v.id);
        return p && p.percentComplete > 0 ? { ...v, progress: p.percentComplete } : v;
      }),
    [videos, progress],
  );

  // Specialisations represented in the corpus.
  const specs = useMemo(() => {
    const seen = new Map<string, { id: string; label: string; count: number }>();
    for (const v of videosWithProgress) {
      if (!v.specId) continue;
      const cur = seen.get(v.specId);
      if (cur) cur.count += 1;
      else seen.set(v.specId, { id: v.specId, label: v.specLabel ?? v.specId, count: 1 });
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [videosWithProgress]);

  // Search covers only what the list endpoint returns. Transcripts live in
  // GCS per video (GET /videos/:id/transcript), so searching them here
  // would mean one fetch per video — "Ask the trainer" covers that instead.
  const filtered = useMemo(() => {
    const matches = videosWithProgress.filter((v) => {
      if (seriesFilter && v.seriesId !== seriesFilter) return false;
      if (specFilter !== 'all' && v.specId !== specFilter) return false;
      if (q) {
        const blob = `${v.title} ${v.description ?? ''} ${v.presenter?.name ?? ''}`.toLowerCase();
        if (!blob.includes(q.toLowerCase())) return false;
      }
      return true;
    });
    return seriesFilter ? matches.sort((a, b) => (a.episode ?? 999) - (b.episode ?? 999)) : matches;
  }, [videosWithProgress, q, specFilter, seriesFilter]);

  const activeSeries = seriesFilter ? series.find((s) => s.id === seriesFilter) : undefined;
  const clearAll = (): void => {
    setQ('');
    setSpecFilter('all');
    if (seriesFilter) setSearchParams({});
  };

  const continueWatching = useMemo(
    () =>
      videosWithProgress
        .filter((v) => v.progress !== undefined && v.progress > 0 && v.progress < 1)
        .sort((a, b) => (b.progress ?? 0) - (a.progress ?? 0))
        .slice(0, 12),
    [videosWithProgress],
  );

  const featured = useMemo(
    () => videosWithProgress.find((v) => v.isFeatured) ?? videosWithProgress[0] ?? null,
    [videosWithProgress],
  );

  // When searching/filtering, fall back to the flat grid even if the user
  // has the rows toggle on — keeps the layout sensible.
  const gridForced = Boolean(q || specFilter !== 'all' || seriesFilter);
  const effectiveView: 'rows' | 'grid' = gridForced ? 'grid' : view;

  return (
    <Layout>
      {error && (
        <div className="mx-6 mt-4 rounded-md border border-nebula-error bg-nebula-error-bg px-4 py-2 text-sm text-nebula-error">
          {error}
        </div>
      )}

      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b border-nebula-border-soft bg-nebula-app px-6 py-3">
        <label className="relative flex w-[340px] items-center">
          <span className="pointer-events-none absolute left-3 text-nebula-fg-4">
            <SearchIcon className="w-4 h-4" />
          </span>
          <input
            type="search"
            placeholder="Search titles, descriptions, presenters…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full rounded-md border border-nebula-border bg-white py-2 pl-9 pr-3 text-sm text-nebula-fg-1 placeholder:text-nebula-fg-4 focus:border-nebula-secondary focus:outline-none"
          />
        </label>

        <div className="flex flex-1 items-center gap-1 overflow-x-auto no-scrollbar">
          <FilterChip
            on={specFilter === 'all'}
            label="All"
            count={videosWithProgress.length}
            onClick={() => setSpecFilter('all')}
          />
          {specs.map((s) => (
            <FilterChip
              key={s.id}
              on={specFilter === s.id}
              color={specColor(s.id)}
              label={s.label}
              count={s.count}
              onClick={() => setSpecFilter(s.id)}
            />
          ))}
        </div>

        <div className="inline-flex gap-0 rounded-md border border-nebula-border-soft bg-white p-0.5">
          <ViewToggle
            on={effectiveView === 'rows'}
            onClick={() => setView('rows')}
            icon="view_carousel"
            // Rows only exist for the unfiltered library; say so rather than
            // leave a toggle that silently does nothing.
            disabled={gridForced}
            title={
              gridForced ? 'Rows are available when no search, filter or series is active' : 'Rows'
            }
          />
          <ViewToggle
            on={effectiveView === 'grid'}
            onClick={() => setView('grid')}
            icon="grid_view"
            title="Grid"
          />
        </div>

        <button
          onClick={() => setAsk({})}
          className="inline-flex items-center gap-1.5 rounded-md border border-nebula-border bg-white px-3 py-1.5 text-sm font-semibold text-nebula-fg-2 hover:bg-nebula-tint"
        >
          <AutoAwesomeIcon className="w-4 h-4 text-nebula-secondary" />
          Ask the trainer
        </button>
      </div>

      <div className="mx-auto max-w-[1300px] px-6 pb-12 pt-5">
        {loading ? (
          <div className="mt-12 text-center text-sm text-nebula-fg-3">Loading training videos…</div>
        ) : videosWithProgress.length === 0 ? (
          <EmptyLibrary isAdmin={user?.role === 'kb_admin'} />
        ) : effectiveView === 'rows' ? (
          <>
            {featured && (
              <HeroFeature
                video={featured}
                series={series}
                onAsk={() => setAsk({ video: featured })}
              />
            )}
            <VideoRow
              title="Continue watching"
              subtitle="Pick up where you left off."
              videos={continueWatching}
            />
            {series.map((s) => {
              const items = videosWithProgress
                .filter((v) => v.seriesId === s.id)
                .sort((a, b) => (a.episode ?? 999) - (b.episode ?? 999));
              return (
                <VideoRow
                  key={s.id}
                  title={s.title}
                  subtitle={s.subtitle}
                  videos={items}
                  action={
                    <Link
                      to={`/videos?seriesId=${encodeURIComponent(s.id)}`}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-nebula-secondary hover:bg-nebula-tint"
                    >
                      See series
                      <ChevronRightIcon className="w-3.5 h-3.5" />
                    </Link>
                  }
                />
              );
            })}
            {/* Catch-all row for videos not in any series */}
            <VideoRow
              title="Standalone videos"
              subtitle="Single-shot training not part of a series."
              videos={videosWithProgress.filter((v) => !v.seriesId)}
            />
          </>
        ) : (
          <>
            {seriesFilter && (
              <SeriesHeader
                series={activeSeries}
                count={videosWithProgress.filter((v) => v.seriesId === seriesFilter).length}
                onBack={() => setSearchParams({})}
              />
            )}
            {filtered.length === 0 ? (
              <NoMatch onClear={clearAll} onAsk={() => setAsk({})} />
            ) : (
              <div
                className="mt-2 grid gap-[18px]"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(244px, 1fr))' }}
              >
                {filtered.map((v) => (
                  <VideoCard key={v.id} video={v} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {ask && (
        <>
          <div onClick={() => setAsk(null)} className="fixed inset-0 z-40 bg-black/20" />
          <aside
            className="fixed inset-y-0 right-0 z-50 flex w-[440px] flex-col bg-white shadow-xl"
            style={{ boxShadow: '-12px 0 32px rgba(14,12,20,0.10)' }}
          >
            <AskPanel
              key={ask.video?.id ?? 'library'}
              videoId={ask.video?.id}
              videoTitle={ask.video?.title}
              defaultScope={ask.video ? 'this' : 'all'}
              // No player on the library page — a timestamp citation opens
              // the video at that moment instead.
              onCiteJump={(vid, t) => navigate(`/videos/${vid}?t=${Math.floor(t)}`)}
            />
          </aside>
        </>
      )}
    </Layout>
  );
}

// ── Hero featured tile ───────────────────────────────────────────────

function HeroFeature({
  video,
  series,
  onAsk,
}: {
  video: Video;
  series: VideoSeries[];
  onAsk: () => void;
}): JSX.Element {
  const ser = series.find((s) => s.id === video.seriesId);
  return (
    <section className="mt-1 grid items-stretch gap-6" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
      <Link
        to={`/videos/${video.id}`}
        className="relative overflow-hidden rounded-md"
        style={{ minHeight: 320 }}
      >
        <VideoThumb video={video} size="xl" showProgress={false} />
        <div
          className="absolute inset-x-0 bottom-0 text-white"
          style={{
            padding: '32px 28px 24px',
            background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.72) 100%)',
          }}
        >
          <div
            className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-white/20 px-2.5 py-1 backdrop-blur"
            style={{ background: 'rgba(255,255,255,0.18)' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
              bookmark
            </span>
            <span className="text-[11px] font-bold uppercase tracking-wider">
              Featured · {ser?.title ?? video.specLabel ?? 'Training'}
            </span>
          </div>
          <div className="text-2xl font-bold leading-tight" style={{ maxWidth: '85%' }}>
            {video.title}
          </div>
          {video.description && (
            <div className="mt-1.5 text-sm" style={{ opacity: 0.88, maxWidth: '80%' }}>
              {video.description}
            </div>
          )}
        </div>
      </Link>

      <div className="flex flex-col rounded-md border border-nebula-border-soft bg-white p-6">
        <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-widest text-nebula-fg-4">
          About this video
        </div>
        {video.presenter && (
          <div className="mb-3.5 flex items-center gap-3">
            <Avatar name={video.presenter.name} size={40} bg={specColor(video.specId)} />
            <div>
              <div className="text-sm font-semibold text-nebula-fg-1">{video.presenter.name}</div>
              {video.presenter.role && (
                <div className="text-xs text-nebula-fg-3">{video.presenter.role}</div>
              )}
            </div>
          </div>
        )}
        <ul className="flex list-none flex-col gap-2 p-0">
          <MetaRow icon="schedule" label="Duration" value={vfmt(video.durationSeconds ?? 0)} />
          {video.publishedAt && (
            <MetaRow
              icon="event"
              label="Published"
              value={new Date(video.publishedAt).toLocaleDateString()}
            />
          )}
          {video.views !== undefined && (
            <MetaRow icon="visibility" label="Views" value={`${video.views} colleagues`} />
          )}
        </ul>
        <div className="flex-1" />
        <div className="mt-4 flex gap-2">
          <Link
            to={`/videos/${video.id}`}
            className="inline-flex items-center gap-1.5 rounded-md bg-nebula-primary px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
          >
            <PlayIcon className="w-4 h-4" />
            {video.progress && video.progress > 0 ? 'Resume' : 'Watch now'}
          </Link>
          <button
            type="button"
            onClick={onAsk}
            className="inline-flex items-center gap-1.5 rounded-md border border-nebula-border bg-white px-4 py-2 text-sm font-semibold text-nebula-fg-2 hover:bg-nebula-tint"
          >
            <AutoAwesomeIcon className="w-4 h-4 text-nebula-secondary" />
            Ask about it
          </button>
        </div>
      </div>
    </section>
  );
}

function MetaRow({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}): JSX.Element {
  return (
    <li className="flex items-center gap-2.5">
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-nebula-gray-6 text-nebula-fg-3">
        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
          {icon}
        </span>
      </span>
      <span className="flex-1 text-xs text-nebula-fg-3">{label}</span>
      <span className="text-sm font-semibold text-nebula-fg-1">{value}</span>
    </li>
  );
}

// ── Tiny avatar (initials) — kept local so it doesn't pull in another file. ──

function Avatar({
  name,
  size = 32,
  bg,
}: {
  name: string;
  size?: number;
  bg?: string;
}): JSX.Element {
  const initials = name
    .split(' ')
    .map((p) => p[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span
      className="inline-flex items-center justify-center text-white"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: bg ?? '#172E57',
        font: `600 ${Math.round(size * 0.4)}px/1 var(--ov-font-display, system-ui)`,
      }}
    >
      {initials || '?'}
    </span>
  );
}

function FilterChip({
  on,
  color,
  label,
  count,
  onClick,
}: {
  on: boolean;
  color?: string;
  label: string;
  count: number;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={
        on
          ? 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-nebula-primary bg-nebula-primary px-3 py-1.5 text-xs font-medium text-white'
          : 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-nebula-border-soft bg-white px-3 py-1.5 text-xs font-medium text-nebula-fg-2 hover:bg-nebula-tint'
      }
    >
      {color && <span className="h-2 w-2 rounded-sm" style={{ background: on ? '#fff' : color }} />}
      <span>{label}</span>
      <span
        className={
          on ? 'text-[11px] font-semibold opacity-80' : 'text-[11px] font-semibold opacity-60'
        }
      >
        {count}
      </span>
    </button>
  );
}

function ViewToggle({
  on,
  onClick,
  icon,
  title,
  disabled = false,
}: {
  on: boolean;
  onClick: () => void;
  icon: string;
  title: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
      className={
        on
          ? 'inline-flex h-7 w-7 items-center justify-center rounded bg-nebula-tint text-nebula-primary'
          : 'inline-flex h-7 w-7 items-center justify-center rounded text-nebula-fg-3 hover:bg-nebula-gray-5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent'
      }
    >
      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
        {icon}
      </span>
    </button>
  );
}

function EmptyLibrary({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  return (
    <div className="mt-24 flex flex-col items-center text-center">
      <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-nebula-tint text-nebula-primary">
        <VideoIcon className="w-7 h-7" />
      </div>
      <div className="mb-1 text-base font-semibold text-nebula-fg-1">No training videos yet</div>
      <div className="max-w-md text-sm text-nebula-fg-3">
        Training videos uploaded by an administrator appear here once indexing completes.
        {isAdmin && (
          <>
            {' '}
            <Link to="/admin/videos" className="font-semibold text-nebula-secondary underline">
              Upload one now
            </Link>
            .
          </>
        )}
      </div>
    </div>
  );
}

function SeriesHeader({
  series,
  count,
  onBack,
}: {
  /** Undefined when the id in the URL matches no series (deleted / typo). */
  series: VideoSeries | undefined;
  count: number;
  onBack: () => void;
}): JSX.Element {
  const episodes = `${count} ${count === 1 ? 'episode' : 'episodes'}`;
  return (
    <div className="mb-4 mt-1">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-nebula-secondary hover:bg-nebula-tint"
      >
        <ArrowBackIcon className="w-4 h-4" />
        All videos
      </button>
      <h1 className="ov-h-page mt-2">{series?.title ?? 'Series not found'}</h1>
      <p className="ov-meta mt-1">
        {series
          ? series.subtitle
            ? `${series.subtitle} · ${episodes}`
            : episodes
          : 'This series is no longer available. Go back to browse all training videos.'}
      </p>
    </div>
  );
}

function NoMatch({ onClear, onAsk }: { onClear: () => void; onAsk: () => void }): JSX.Element {
  return (
    <div className="mt-12 flex flex-col items-center text-center">
      <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-nebula-gray-5 text-nebula-fg-3">
        <SearchOffIcon className="w-7 h-7" />
      </div>
      <div className="mb-1 text-base font-semibold text-nebula-fg-1">No videos match</div>
      <div className="mb-4 max-w-md text-sm text-nebula-fg-3">
        Search covers titles, descriptions and presenters. To find something said inside a video,
        ask the trainer.
      </div>
      <div className="flex gap-2">
        <button
          onClick={onClear}
          className="inline-flex items-center gap-1.5 rounded-md border border-nebula-border bg-white px-3 py-1.5 text-xs font-semibold text-nebula-fg-2 hover:bg-nebula-tint"
        >
          Clear filters
        </button>
        <button
          onClick={onAsk}
          className="inline-flex items-center gap-1.5 rounded-md border border-nebula-border bg-white px-3 py-1.5 text-xs font-semibold text-nebula-fg-2 hover:bg-nebula-tint"
        >
          <AutoAwesomeIcon className="w-4 h-4 text-nebula-secondary" />
          Ask the trainer
        </button>
      </div>
    </div>
  );
}
